import React from 'react';
import {
  Typography, TextField, Grid, Button, MenuItem, Box, CircularProgress, Alert,
  FormControl, FormLabel, FormGroup, FormControlLabel, Checkbox, FormHelperText, RadioGroup, Radio,
} from '@mui/material';
import { US_STATES } from '../utils/constants';
import axios from '../utils/api';
import {
  ServiceProviderFormState,
  ServiceProviderValidationErrors,
  ORG_TYPE_OPTIONS,
  COST_TYPE_OPTIONS,
  formatPhoneNumber,
} from './serviceProviderShared';

const DOCUMENT_MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const DOCUMENT_ACCEPTED_EXTENSIONS = ['.pdf', '.docx'];
const DOCUMENT_ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const DOCUMENT_ACCEPT_ATTR =
  '.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

type DocumentUploadField = 'eligibilityCriteria' | 'servicesProvided';

function validateDocumentFile(file: File): string | null {
  const lowerName = file.name.toLowerCase();
  const extensionOk = DOCUMENT_ACCEPTED_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
  const mimeOk = DOCUMENT_ACCEPTED_MIME_TYPES.includes(file.type);
  if (!extensionOk && !mimeOk) {
    return 'Unsupported file type. Please upload a PDF or Word (.docx) document.';
  }
  if (file.size > DOCUMENT_MAX_UPLOAD_BYTES) {
    return 'File is too large. Maximum size is 10 MB.';
  }
  return null;
}

interface Props {
  form: ServiceProviderFormState;
  setForm: React.Dispatch<React.SetStateAction<ServiceProviderFormState>>;
  errors: ServiceProviderValidationErrors;
  clearError: (field: keyof ServiceProviderValidationErrors) => void;
  // When true, the registration email is shown but locked (self-service edit)
  registrantEmailReadOnly?: boolean;
}

const ServiceProviderFormFields: React.FC<Props> = ({ form, setForm, errors, clearError, registrantEmailReadOnly }) => {
  const [parsingEligibility, setParsingEligibility] = React.useState(false);
  const [eligibilityUploadError, setEligibilityUploadError] = React.useState<string | null>(null);
  const [eligibilityUploadWarning, setEligibilityUploadWarning] = React.useState<string | null>(null);
  const [eligibilityFileName, setEligibilityFileName] = React.useState<string | null>(null);

  const [parsingServices, setParsingServices] = React.useState(false);
  const [servicesUploadError, setServicesUploadError] = React.useState<string | null>(null);
  const [servicesUploadWarning, setServicesUploadWarning] = React.useState<string | null>(null);
  const [servicesFileName, setServicesFileName] = React.useState<string | null>(null);

  const uploadDocumentForField = async (
    file: File,
    field: DocumentUploadField,
    errorFallback: string,
    setParsing: React.Dispatch<React.SetStateAction<boolean>>,
    setFileName: React.Dispatch<React.SetStateAction<string | null>>,
    setUploadError: React.Dispatch<React.SetStateAction<string | null>>,
    setUploadWarning: React.Dispatch<React.SetStateAction<string | null>>
  ) => {
    setUploadError(null);
    setUploadWarning(null);

    const validationError = validateDocumentFile(file);
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setParsing(true);
    setFileName(file.name);
    try {
      const data = new FormData();
      data.append('document', file);
      const res = await axios.post('/api/service-providers/parse-document', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const text: string = res.data?.text ?? '';
      if (text) {
        setForm((prev) => ({ ...prev, [field]: text }));
        clearError(field);
      }
      if (res.data?.warning) {
        setUploadWarning(res.data.warning);
      }
    } catch (err: any) {
      setFileName(null);
      setUploadError(err?.response?.data?.error || errorFallback);
    } finally {
      setParsing(false);
    }
  };

  const handleEligibilityFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    await uploadDocumentForField(
      file,
      'eligibilityCriteria',
      'Failed to read the document. Please try again or type the criteria manually.',
      setParsingEligibility,
      setEligibilityFileName,
      setEligibilityUploadError,
      setEligibilityUploadWarning
    );
  };

  const handleServicesFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    await uploadDocumentForField(
      file,
      'servicesProvided',
      'Failed to read the document. Please try again or type the services manually.',
      setParsingServices,
      setServicesFileName,
      setServicesUploadError,
      setServicesUploadWarning
    );
  };

  const handleField = (field: keyof ServiceProviderFormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    clearError(field as keyof ServiceProviderValidationErrors);
  };

  const handlePhoneBlur = () => {
    if (form.phone.trim()) {
      setForm((prev) => ({ ...prev, phone: formatPhoneNumber(prev.phone) }));
    }
  };

  const toggleArrayValue = (field: 'organizationTypes' | 'serviceCostTypes', value: string) => {
    setForm((prev) => {
      const set = new Set(prev[field]);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return { ...prev, [field]: Array.from(set) };
    });
    clearError(field);
  };

  return (
    <Grid container spacing={2}>
      {/* Organization name */}
      <Grid item xs={12}>
        <TextField
          label="Organization Name"
          name="organizationName"
          value={form.organizationName}
          onChange={handleField('organizationName')}
          fullWidth
          required
          error={!!errors.organizationName}
          helperText={errors.organizationName}
        />
      </Grid>

      {/* Organization types (multi-select) */}
      <Grid item xs={12} id="sp-anchor-organizationTypes">
        <FormControl component="fieldset" error={!!errors.organizationTypes} required>
          <FormLabel component="legend">Organization Type</FormLabel>
          <RadioGroup row>
            {ORG_TYPE_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.value}
                control={
                  <Radio
                    value={opt.value}
                    onChange={() => toggleArrayValue('organizationTypes', opt.value)}
                  />
                }
                label={opt.label}
              />
            ))}
          </RadioGroup>
          {errors.organizationTypes && <FormHelperText>{errors.organizationTypes}</FormHelperText>}
        </FormControl>
      </Grid>
      {form.organizationTypes.includes('other') && (
        <Grid item xs={12} sm={6}>
          <TextField
            label="Please specify"
            name="organizationTypeOther"
            value={form.organizationTypeOther}
            onChange={handleField('organizationTypeOther')}
            fullWidth
            required
            error={!!errors.organizationTypeOther}
            helperText={errors.organizationTypeOther}
          />
        </Grid>
      )}

      {/* Address */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mt: 2 }}>Address</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Address Line 1"
          name="addressLine1"
          value={form.addressLine1}
          onChange={handleField('addressLine1')}
          fullWidth
          required
          error={!!errors.addressLine1}
          helperText={errors.addressLine1}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Address Line 2"
          value={form.addressLine2}
          onChange={handleField('addressLine2')}
          fullWidth
          helperText="Optional"
        />
      </Grid>
      <Grid item xs={12} sm={5}>
        <TextField
          label="City"
          name="city"
          value={form.city}
          onChange={handleField('city')}
          fullWidth
          required
          error={!!errors.city}
          helperText={errors.city}
        />
      </Grid>
      <Grid item xs={12} sm={4} id="sp-anchor-state">
        <TextField
          select
          label="State"
          value={form.state}
          onChange={handleField('state')}
          fullWidth
          required
          error={!!errors.state}
          helperText={errors.state}
        >
          {US_STATES.map((state) => (
            <MenuItem key={state} value={state}>{state}</MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Zip Code"
          name="zipCode"
          value={form.zipCode}
          onChange={handleField('zipCode')}
          fullWidth
          required
          error={!!errors.zipCode}
          helperText={errors.zipCode}
        />
      </Grid>

      {/* Phone */}
      <Grid item xs={12} sm={6}>
        <TextField
          label="Phone"
          name="phone"
          value={form.phone}
          onChange={handleField('phone')}
          onBlur={handlePhoneBlur}
          fullWidth
          required
          type="tel"
          inputProps={{ maxLength: 20 }}
          error={!!errors.phone}
          helperText={errors.phone || 'Format: (xxx) xxx-xxxx'}
        />
      </Grid>

      {/* Registrant */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mt: 2 }}>Person Registering</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Your Name"
          name="registrantName"
          value={form.registrantName}
          onChange={handleField('registrantName')}
          fullWidth
          required
          error={!!errors.registrantName}
          helperText={errors.registrantName}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Your Email (used to sign in and manage this registration)"
          name="registrantEmail"
          value={form.registrantEmail}
          onChange={handleField('registrantEmail')}
          fullWidth
          required
          type="email"
          disabled={registrantEmailReadOnly}
          error={!!errors.registrantEmail}
          helperText={registrantEmailReadOnly ? 'Email cannot be changed' : errors.registrantEmail}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Contact Email for Service Requests from Public"
          name="publicServicesEmail"
          value={form.publicServicesEmail}
          onChange={handleField('publicServicesEmail')}
          fullWidth
          type="email"
          error={!!errors.publicServicesEmail}
          helperText={errors.publicServicesEmail || 'Optional - if different from your registration email'}
        />
      </Grid>

      {/* Authorizer */}
      {/*<Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mt: 2 }}>Authorizing Official</Typography>
        <Typography variant="body2" color="text.secondary">
          The person at your organization who authorized you to register.
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Name"
          name="authorizerName"
          value={form.authorizerName}
          onChange={handleField('authorizerName')}
          fullWidth
          required
          error={!!errors.authorizerName}
          helperText={errors.authorizerName}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Title"
          name="authorizerTitle"
          value={form.authorizerTitle}
          onChange={handleField('authorizerTitle')}
          fullWidth
          required
          error={!!errors.authorizerTitle}
          helperText={errors.authorizerTitle}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Email"
          name="authorizerEmail"
          value={form.authorizerEmail}
          onChange={handleField('authorizerEmail')}
          fullWidth
          required
          type="email"
          error={!!errors.authorizerEmail}
          helperText={errors.authorizerEmail}
        />
      </Grid> */}

      {/* Cost types (multi-select) */}
      <Grid item xs={12} id="sp-anchor-serviceCostTypes">
        <FormControl component="fieldset" error={!!errors.serviceCostTypes} required>
          <FormLabel component="legend">Which do you provide? (select all that apply)</FormLabel>
          <FormGroup row>
            {COST_TYPE_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.value}
                control={
                  <Checkbox
                    checked={form.serviceCostTypes.includes(opt.value)}
                    onChange={() => toggleArrayValue('serviceCostTypes', opt.value)}
                  />
                }
                label={opt.label}
              />
            ))}
          </FormGroup>
          {errors.serviceCostTypes && <FormHelperText>{errors.serviceCostTypes}</FormHelperText>}
        </FormControl>
      </Grid>

      {/* Description of services provided */}
      <Grid item xs={12}>
        <TextField
          label="Services Provided - Type here or upload a document in PDF or Word format"
          name="servicesProvided"
          value={form.servicesProvided}
          onChange={handleField('servicesProvided')}
          fullWidth
          multiline
          required
          minRows={3}
          InputLabelProps={{ shrink: true }}
          error={!!errors.servicesProvided}
          helperText={
            errors.servicesProvided ||
            'Specify the service(s) your organization provides'
          }
        />
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Button
            component="label"
            variant="contained"
            size="small"
            disabled={parsingServices}
          >
            {parsingServices ? 'Reading document…' : 'Upload Services Document (PDF or Word)'}
            <input
              type="file"
              hidden
              accept={DOCUMENT_ACCEPT_ATTR}
              onChange={handleServicesFile}
            />
          </Button>
          {parsingServices && <CircularProgress size={20} />}
          {!parsingServices && servicesFileName && (
            <Typography variant="body2" color="text.secondary">
              Loaded from: {servicesFileName}
            </Typography>
          )}
        </Box>
        {servicesUploadError && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setServicesUploadError(null)}>
            {servicesUploadError}
          </Alert>
        )}
        {servicesUploadWarning && (
          <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setServicesUploadWarning(null)}>
            {servicesUploadWarning}
          </Alert>
        )}
      </Grid>

      {/* Jurisdiction where services are provided */}
      <Grid item xs={12}>
        <TextField
          label="Jurisdiction(s) Where Services Are Provided"
          name="serviceJurisdiction"
          value={form.serviceJurisdiction}
          onChange={handleField('serviceJurisdiction')}
          fullWidth
          multiline
          required
          minRows={3}
          InputLabelProps={{ shrink: true }}
          error={!!errors.serviceJurisdiction}
          helperText={
            errors.serviceJurisdiction ||
            "Specify the geographic area(s) your services cover"
          }
        />
      </Grid>

      {/* Eligibility criteria */}
      <Grid item xs={12}>
        <TextField
          label="Eligibility Criteria - Type here or upload a document in PDF or Word format"
          name="eligibilityCriteria"
          value={form.eligibilityCriteria}
          onChange={handleField('eligibilityCriteria')}
          fullWidth
          multiline
          required
          minRows={3}
          InputLabelProps={{ shrink: true }}
          error={!!errors.eligibilityCriteria}
          helperText={
            errors.eligibilityCriteria ||
            'Describe who is eligible to receive your services.'
          }
        />
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Button
            component="label"
            variant="contained"
            size="small"
            disabled={parsingEligibility}
          >
            {parsingEligibility ? 'Reading document…' : 'Upload Eligibility Document (PDF or Word)'}
            <input
              type="file"
              hidden
              accept={DOCUMENT_ACCEPT_ATTR}
              onChange={handleEligibilityFile}
            />
          </Button>
          {parsingEligibility && <CircularProgress size={20} />}
          {!parsingEligibility && eligibilityFileName && (
            <Typography variant="body2" color="text.secondary">
              Loaded from: {eligibilityFileName}
            </Typography>
          )}
        </Box>
        {eligibilityUploadError && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setEligibilityUploadError(null)}>
            {eligibilityUploadError}
          </Alert>
        )}
        {eligibilityUploadWarning && (
          <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setEligibilityUploadWarning(null)}>
            {eligibilityUploadWarning}
          </Alert>
        )}
      </Grid>

    </Grid>
  );
}; 

export default ServiceProviderFormFields;
